/**
 * 69. Employee Record — personal, employment, compensation,
 * documents, leave balance; field-level visibility.
 */
import { useParams } from "react-router-dom";
import {
  Card,
  PageHeader,
  Button,
  Skeleton,
  DataTable,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiQuery } from "@/lib/api/hooks";
import { formatDate, formatMoney } from "@/lib/money";

interface EmployeeDetail {
  id: string;
  employeeNumber: string;
  displayName: string;
  email: string;
  department: string;
  jobTitle: string;
  hireDate: string | null;
  status: "Active" | "OnLeave" | "Suspended" | "Terminated";
  salary: string;
  currency: string;
  leaveBalanceDays: number;
  documents: Array<{
    id: string;
    name: string;
    type: string;
    uploadedAt: string;
  }>;
}

export function EmployeeRecordPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useApiQuery<EmployeeDetail>(
    ["hr", "employees", id ?? ""],
    `/hr/employees/${id}`,
  );

  if (isLoading || !data) return <Skeleton rows={10} />;

  return (
    <div>
      <PageHeader
        title={data.displayName}
        subtitle={`${data.jobTitle} · ${data.department} · ${data.employeeNumber}`}
        actions={<Button variant="primary">Edit record</Button>}
        breadcrumb={["HR", "Employee Directory", data.displayName]}
      />

      <div className="atlas-grid atlas-grid--2 mb-4">
        <Card title="Employment">
          <dl
            style={{
              display: "grid",
              gridTemplateColumns: "160px 1fr",
              gap: 8,
              fontSize: 14,
            }}
          >
            <dt className="atlas-muted">Employee number</dt>
            <dd className="atlas-mono">{data.employeeNumber}</dd>
            <dt className="atlas-muted">Email</dt>
            <dd>{data.email}</dd>
            <dt className="atlas-muted">Hire date</dt>
            <dd>{formatDate(data.hireDate)}</dd>
            <dt className="atlas-muted">Base salary</dt>
            <dd>{formatMoney(data.salary, data.currency)}</dd>
            <dt className="atlas-muted">Leave balance</dt>
            <dd>{data.leaveBalanceDays} days</dd>
            <dt className="atlas-muted">Status</dt>
            <dd>
              {data.status === "Active" ? (
                <StatusBadge status={data.status} />
              ) : (
                <Badge
                  tone={
                    data.status === "OnLeave"
                      ? "warning"
                      : data.status === "Terminated"
                        ? "danger"
                        : "default"
                  }
                >
                  {data.status}
                </Badge>
              )}
            </dd>
          </dl>
        </Card>
        <Card title="Documents">
          <DataTable
            rows={data.documents}
            keyField="id"
            columns={[
              { key: "name", label: "Name" },
              { key: "type", label: "Type" },
              {
                key: "uploadedAt",
                label: "Uploaded",
                render: (r) => formatDate(r.uploadedAt),
              },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}
